import React from "react";
import Icon from "./Icon";
import useResponsive from "../../../_hooks/useResponsive";
import cc from "classcat";

const Button = React.forwardRef(
	(
		{
			className = "",
			children,
			isLoading = false,
			hasIcon = false,
			disabled = false,
			noHover = false,
			large = false,
			...props
		},
		ref
	) => (
		<button
			disabled={disabled}
			className={cc([
				"btn btn-component",
				className,
				{
					"btn-no-hover": noHover,
					"btn-has-icon pl-3": hasIcon,
					"is-loading": isLoading,
					"btn-disabled": disabled,
					"btn-lg": large,
				},
			])}
			{...props}
			ref={ref}
		>
			<span className="btn-content">{children}</span>
		</button>
	)
);

const PrimaryButton = React.forwardRef(({ className = "", ...props }, ref) => (
	<Button className={cc(["btn-primary", className])} {...props} ref={ref} />
));

const SecondaryButton = React.forwardRef(({ className = "", ...props }, ref) => (
	<Button className={cc(["btn-secondary", className])} {...props} ref={ref} />
));

const TertiaryButton = React.forwardRef(({ className = "", ...props }, ref) => (
	<Button className={cc(["btn-tertiary", className])} {...props} ref={ref} />
));

const PremiumButton = React.forwardRef(({ className = "", onClick, children, ...props }, ref) => (
	<Button className={cc(["btn-premium", className])} {...props} isLoading={false} ref={ref} onClick={onClick}>
		{children}
	</Button>
));

const GreenButton = React.forwardRef(({ className = "", ...props }, ref) => (
	<Button className={cc(["btn-green", className])} {...props} ref={ref} />
));

const ActionButton = React.forwardRef(
	(
		{ children, className = "", icon = null, iconProps = {}, numericCount = null, hideToSide = false, ...props },
		ref
	) => {
		const isMobile = useResponsive();
		return (
			<Button
				className={cc([
					"btn-action btn-no-hover",
					className,
					{
						"hide-to-side": hideToSide,
						"has-count": numericCount !== null,
					},
				])}
				{...props}
				ref={ref}
				hasIcon={icon !== null}
			>
				{icon && (
					<span
						className={cc([
							{
								"mr-1": numericCount,
								"mr-0": !numericCount,
							},
							"d-inline-block",
						])}
						style={{
							width: iconProps.width || 24,
							height: iconProps.height || 24,
							lineHeight: "1",
						}}
					>
						<Icon name={icon} {...{ width: 24, height: 24, color: "gray-white", ...iconProps }} />
					</span>
				)}
				{numericCount !== null && (
					<span
						className={cc([
							"text-gray-white medium-text font-weight-bold d-inline-block",
							{
								"mr-8": isMobile,
							},
						])}
					>
						{numericCount}
					</span>
				)}
				{!isMobile && <span className="d-inline-block ml-2">{children}</span>}
			</Button>
		);
	}
);

const CloseButton = ({ color = null, className = "", ...props }) => (
	<button className={cc(["btn-clear btn-close cursor-pointer", className])} aria-label="Close" {...props}>
		<Icon name="close" color={color} />
	</button>
);

export {
	Button,
	PrimaryButton,
	SecondaryButton,
	TertiaryButton,
	PremiumButton,
	ActionButton,
	CloseButton,
	GreenButton,
};
